import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import type { MediaKind, MediaRef } from "@velizhanin-dev/bot-scenario"
import { useMemo, useState } from "react"
import { toast } from "sonner"
import { type MediaSource, scenarioMediaSource } from "@/entities/bot-builder"
import { uploadFileToS3 } from "@/shared/api/upload-to-s3"

/**
 * Как Telegram может отправить файл такого типа; первый вариант: по умолчанию.
 * Правила размеров и форматов для каждого вида проверяет api при загрузке.
 */
export function mediaKindsFor(mime: string): MediaKind[] {
	if (/^image\/(jpeg|png)$/.test(mime)) return ["photo", "document"]
	if (mime === "image/webp") return ["photo", "sticker", "document"]
	if (mime === "image/gif") return ["animation", "document"]
	if (mime === "video/mp4") return ["video", "animation", "video_note", "document"]
	if (mime === "audio/ogg") return ["voice", "document"]
	if (/^audio\/(mpeg|mp4|x-m4a|m4a)$/.test(mime)) return ["audio", "voice", "document"]
	if (mime === "application/x-tgsticker" || mime === "video/webm") return ["sticker", "document"]
	return ["document"]
}

/** Файлы сценария или рассылки: откуда: решает вызывающий через source. */
export function useMediaSource(scenarioId: string | undefined, source: MediaSource | undefined) {
	return useMemo(() => source ?? scenarioMediaSource(scenarioId ?? ""), [scenarioId, source])
}

export function useUpload(source: MediaSource) {
	const [progress, setProgress] = useState<number | null>(null)
	const upload = async (file: File, kind?: MediaKind): Promise<MediaRef | null> => {
		const mime = file.type || "application/octet-stream"
		// Content-Type загрузки должен совпасть с тем, под который подписана ссылка.
		const typed = file.type ? file : new File([file], file.name, { type: mime })
		try {
			setProgress(0)
			const presigned = await source.presign({
				kind: kind ?? mediaKindsFor(mime)[0],
				fileName: file.name,
				mime,
				size: file.size,
			})
			await uploadFileToS3(presigned.uploadUrl, typed, setProgress)
			return presigned.media
		} catch (error) {
			toast.error(extractApiErrorMessage(error, `Не удалось загрузить ${file.name}`))
			return null
		} finally {
			setProgress(null)
		}
	}
	return { progress, upload }
}
